// this file handles calculating the inaccessible compartment voxels needed to generate the desired geometry shape of the model.
// currently can create spheres and channels

// distance between two points [x,y,z]
function getDistance(p1: Array<number>, p2: Array<number>): number {
	return (Math.pow(Math.pow(p1[0] - p2[0], 2) + Math.pow(p1[1] - p2[1], 2) + Math.pow(p1[2] - p2[2], 2), 1 / 2))
}

// find midpoint of the simulation space, whole integers not required
function getMidpoint(x: number, y: number, z: number): Array<number> {
	let midpoint = [x / 2 - 0.5, y / 2 - 0.5, z / 2 - 0.5];
	return (midpoint)
}


function voxelInSphere(distance: number, radius: number): Boolean {
	return (distance < radius)
}

// creates a negative space of a sphere centered on the midpoint of the simulation space by carving away voxels on the 8 corners
// radius input should be the shortest axis of the space, but technically can be anything
export function getSphereCoords(xDim: number, yDim: number, zDim: number, radius: number): Array<Array<number>> {
	// let x_lim = Math.ceil(xDim / 2)
	// let y_lim = Math.ceil(yDim / 2)
	let z_lim = Math.ceil(zDim / 2)
	let coord_array: Array<Array<number>> = [];
	for (let current_x = 0; current_x < xDim; current_x++) {
		for (let current_y = 0; current_y < yDim; current_y++) {
			let new_coord = [current_x, current_y, 0];
			// if the very first z coord is already in sphere, don't add this set to the inaccessible list
			let add_coord = false;
			for (let current_z = 0; current_z < z_lim; current_z++) {
				let distance = getDistance([current_x, current_y, current_z], getMidpoint(xDim, yDim, zDim));
				let inSphere = voxelInSphere(distance, radius);
				if (inSphere) {
					break;
				}
				new_coord[2] = current_z;
				add_coord = true;
			}
			if (add_coord) {
				coord_array.push(new_coord);
			}
		}
	}
	let normal_array = coord_array.map((coord: Array<number>, index: number) => {
		let coord_set = [coord[0], coord[1], 0, ...coord]
		return (coord_set);
	});
	let z_mirrored_array = coord_array.map((coord: Array<number>, index: number) => {
		coord[2] = zDim - 1 - coord[2];
		let coord_set = [...coord, coord[0], coord[1], Number(zDim) - 1]
		return (coord_set);
	});
	return ([...normal_array, ...z_mirrored_array])
}

// this one is more complicated, the direction of the tunnel will be along the longest axis
// don't use pre-defined x/y/z coords until the longest axis is figured out, then draw tunnel along it
// by default the tunnel will be along x axis if all dimensions are equal
// tunnel can be formed by carving out 4 rectangular prisms that surround the tunnel
// minimum tunnel length is 1, so longest axis must be at least 3
export function getChannelCoords(xDim: number, yDim: number, zDim: number): Array<Array<number>> {
	// tunnel should leave 1 width for chambers on either end, so max length is total_length - 2
	// after that, tunnel should be >= 1/3 of the total length. side chambers should be 1/3 total rounded down, both being equal width. 
	let dimensions: any = [xDim, yDim, zDim];
	let longest_axis = Math.max(...dimensions);
	let chamber_width: number = Math.floor(longest_axis / 3);
	let channel_length: number = longest_axis - 2 * chamber_width;

	let longest_index: any = dimensions.indexOf(longest_axis.toString());
	if (longest_index >= 0) {
		dimensions.splice(longest_index, 1);
	}

	let channel_axis1_pos = Math.floor(dimensions[0] / 2 - 0.5);
	let channel_axis2_pos = Math.floor(dimensions[1] / 2 - 0.5);

	// origin to channel, sheet width is origin to before channel
	let shape1: Array<number> = [0, 0, channel_axis1_pos - 1, dimensions[1]];
	// top of channel, 1 width
	let shape2: Array<number> = [channel_axis1_pos, channel_axis2_pos + 1, channel_axis1_pos, dimensions[1]];
	// bottom of channel, 1 width
	let shape3: Array<number> = [channel_axis1_pos, 0, channel_axis1_pos, channel_axis2_pos - 1];
	// channel to end, sheet width is after channel to end
	let shape4: Array<number> = [channel_axis1_pos + 1, 0, dimensions[0], dimensions[1]];
	if (![0, 1, 2].includes(longest_index)) {
		shape1 = [];
		shape2 = [];
		shape3 = [];
		shape4 = [];
	} else {
		shape1 = [...shape1.slice(0, longest_index), chamber_width, ...shape1.slice(longest_index, longest_index + 2), chamber_width + channel_length - 1, ...shape1.slice(longest_index + 2)];
		shape2 = [...shape2.slice(0, longest_index), chamber_width, ...shape2.slice(longest_index, longest_index + 2), chamber_width + channel_length - 1, ...shape2.slice(longest_index + 2)];
		shape3 = [...shape3.slice(0, longest_index), chamber_width, ...shape3.slice(longest_index, longest_index + 2), chamber_width + channel_length - 1, ...shape3.slice(longest_index + 2)];
		shape4 = [...shape4.slice(0, longest_index), chamber_width, ...shape4.slice(longest_index, longest_index + 2), chamber_width + channel_length - 1, ...shape4.slice(longest_index + 2)];
	}


	let out_array = [shape1, shape2, shape3, shape4];
	return (out_array);
}


// This will form a 1 layer thick sheet along the XY plane, at Z axis midpoint
export function getLayerCoords(xDim: number, yDim: number, zDim: number): Array<Array<number>> {

	let midpoint = getMidpoint(xDim, yDim, zDim);

	// origin to below layer
	let shape1: Array<number> = [0, 0, 0, xDim, yDim, Math.floor(midpoint[2]) - 1];
	// above layer to top
	let shape2: Array<number> = [0, 0, Math.floor(midpoint[2]) + 1, xDim, yDim, zDim];




	let out_array = [shape1, shape2];
	return (out_array);
}
